import { newestLockedVersion } from '../creatives/ad-versions';
import { listBannerKits } from '../creatives/banner-kit-api';
import { kitPosterUrl } from '../creatives/components/BannerKitLibrary/BannerKitLibrary';
import { html5BannerPoster } from '../creatives/html5-banners';
import { readLivePages } from '../creatives/landing-page-api';
import {
  fetchPlayableTemplates,
  isHtml5Banner,
  isMiniGame,
  listPlayables,
  type Playable,
} from '../creatives/playable-api';
import { posterVersion } from '../creatives/playable-poster';
import { listVideos, videoCardState, videosForPipeline } from '../creatives/video-library-api';
import type { MarketingAreaId } from './areas';

/**
 * Each Marketing area's library read, as its hub card needs it: the live (non-archived) ads, newest first, each with
 * its run's job id (the card asks the job store whether it is generating), whether it has a generated version, and
 * its poster: the image its own library card shows, '' while nothing has rendered (never the subject key art a draft
 * was minted with, which would pass an unmade ad off as a finished one). The same calls the libraries open with.
 * A read throws when the backend cannot be reached, so the card says nothing rather than "none yet".
 */

export type AdCardRow = { jobId?: string; generated: boolean; poster: string };

type AdCardRead = (tenantId: number, signal: AbortSignal) => Promise<AdCardRow[]>;

/** The playables of one tab: HTML5 banners are the playables of a `banner` template, mini-games the rest. */
const readPlayables = async (
  tenantId: number,
  signal: AbortSignal,
  belongs: typeof isHtml5Banner,
  poster: (p: Playable) => string,
): Promise<AdCardRow[]> => {
  const [rows, registry] = await Promise.all([listPlayables(tenantId, signal), fetchPlayableTemplates()]);
  // Without the template registry the two tabs cannot be told apart, so neither card can count its own.
  if (!registry) throw new Error('playable templates unavailable');
  return rows
    .filter((p) => !p.archived && belongs(p, registry.templates))
    .map((p) => ({ jobId: p.job_id, generated: Boolean(newestLockedVersion(p.ad_versions)), poster: poster(p) }));
};

export const AD_CARD_READS: Record<MarketingAreaId, AdCardRead> = {
  'banner-kits': async (tenantId, signal) =>
    (await listBannerKits(tenantId, signal))
      .filter((kit) => !kit.archived)
      .map((kit) => ({
        jobId: kit.job_id,
        generated: Boolean(newestLockedVersion(kit.ad_versions)),
        poster: kitPosterUrl(kit),
      })),
  'html5-banners': (tenantId, signal) => readPlayables(tenantId, signal, isHtml5Banner, html5BannerPoster),
  // The Video Ads tab lists the storyboard (v1) ads only; v2.0 single clips have no tab or card for now.
  videos: async (tenantId, signal) =>
    videosForPipeline(await listVideos(tenantId, signal), 'beats')
      .filter((video) => !video.archived)
      .map((video) => {
        const generated = videoCardState(video) === 'video';
        // A rendered ad's key visual is its first frame; a draft's is only the promotion art it was started from.
        return { jobId: video.job_id, generated, poster: generated ? video.image_url : '' };
      }),
  playables: (tenantId, signal) =>
    readPlayables(tenantId, signal, isMiniGame, (p) => posterVersion(p)?.assets.background_url ?? ''),
  // The pages live on the brand's landing domain: each one is published (never a draft or a run), with no image of its
  // own to show (its library card draws the page itself).
  'landing-pages': async (tenantId, signal) =>
    (await readLivePages(tenantId, signal)).pages.map(() => ({ generated: true, poster: '' })),
};
